import { computed, unref, type ComputedRef, type MaybeRef } from 'vue';
import { injectLocal } from '@vueuse/core';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import useExperiments from '~/stores/useExperimentsStore';
import { useBrand } from '~/composables/useBrand';
import { PRODUCT_FLAGS_REPOSITORY, PRODUCT_FEATURE_ELIGIBILITY_REPOSITORY } from '~/modules/product/constants/providers';
import type { ExperimentId } from '~~/server/experiments';

const GET_IT_TODAY_EXP1_ID: ExperimentId = '3158-21';
const GET_IT_TODAY_EXP2_ID: ExperimentId = '3158-22';

export default function useGetItTodayExperiments(isAvailabilityEnabled?: MaybeRef<boolean>): {
  isGetItTodayExp1Enabled: ComputedRef<boolean>;
  isGetItTodayExp2Enabled: ComputedRef<boolean>;
  isChipFulfillmentEnabled: ComputedRef<boolean>;
  shouldRenderAvailability: ComputedRef<boolean>;
} {
  const { killswitches } = useKillswitches();
  const experiments = useExperiments();
  const { isMcom } = useBrand();

  const flags = injectLocal(PRODUCT_FLAGS_REPOSITORY) as Record<string, boolean> | undefined;
  const eligibility = injectLocal(PRODUCT_FEATURE_ELIGIBILITY_REPOSITORY) as Record<string, boolean> | undefined;

  const isBigTicketItemFlag = flags?.isBigTicketItemFlag ?? false;
  const isCountryEligibleFlag = flags?.isCountryEligibleFlag ?? true;
  const isInternationalMode = eligibility?.isInternationalMode ?? false;

  const isEligible = computed(() => {
    if (isBigTicketItemFlag) return false;
    if (isMcom && isInternationalMode) return false;
    if (!isCountryEligibleFlag) return false;
    return true;
  });

  const isExperimentEnabled = (treatment: 'getItTodayTreatment1Enabled' | 'getItTodayTreatment2Enabled', experimentId: ExperimentId) =>
    Boolean(isEligible.value && killswitches.getItTodayEnabled && (killswitches[treatment] || unref(experiments.experimentIds)?.has(experimentId)));

  const isGetItTodayExp1Enabled = computed(() => isExperimentEnabled('getItTodayTreatment1Enabled', GET_IT_TODAY_EXP1_ID));
  const isGetItTodayExp2Enabled = computed(() => isExperimentEnabled('getItTodayTreatment2Enabled', GET_IT_TODAY_EXP2_ID));
  const isChipFulfillmentEnabled = computed(() => isGetItTodayExp1Enabled.value || isGetItTodayExp2Enabled.value);
  const shouldRenderAvailability = computed(() => isGetItTodayExp1Enabled.value || Boolean(unref(isAvailabilityEnabled)));

  return {
    isGetItTodayExp1Enabled,
    isGetItTodayExp2Enabled,
    isChipFulfillmentEnabled,
    shouldRenderAvailability,
  };
}
